/**
 * Guidance Gantt path highlight — forward / backward dependency chains and critical path.
 */

import type { GuidanceTreeNode } from './types';
import type { FlatGuidanceRow } from './derivation';
import type { SvgPathSpec } from './guidanceGanttHelpers';

export type GuidancePathDisplayMode = 'dim' | 'hide';

export type GuidancePathMode = 'forward' | 'backward' | 'critical';

export const GUIDANCE_PATH_DIM_OPACITY = 0.28;

interface ActivityGraph {
  activities: GuidanceTreeNode[];
  predBySucc: Map<string, string>;
  succByPred: Map<string, string[]>;
  parentId: Map<string, string>;
}

function indexActivityGraph(root: GuidanceTreeNode): ActivityGraph {
  const activities: GuidanceTreeNode[] = [];
  const parentId = new Map<string, string>();
  const predBySucc = new Map<string, string>();
  const succByPred = new Map<string, string[]>();

  const walk = (node: GuidanceTreeNode, parent?: string) => {
    if (parent) parentId.set(node.id, parent);
    if (node.type === 'activity') {
      activities.push(node);
      if (node.dep) {
        predBySucc.set(node.id, node.dep);
        const list = succByPred.get(node.dep) ?? [];
        list.push(node.id);
        succByPred.set(node.dep, list);
      }
    }
    (node.children ?? []).forEach((child) => walk(child, node.id));
  };
  walk(root);

  return { activities, predBySucc, succByPred, parentId };
}

function forwardClosure(focusId: string, succByPred: Map<string, string[]>): Set<string> {
  const out = new Set<string>([focusId]);
  const queue = [focusId];
  while (queue.length > 0) {
    const id = queue.shift()!;
    for (const succ of succByPred.get(id) ?? []) {
      if (!out.has(succ)) {
        out.add(succ);
        queue.push(succ);
      }
    }
  }
  return out;
}

function backwardClosure(focusId: string, predBySucc: Map<string, string>): Set<string> {
  const out = new Set<string>();
  let cur: string | undefined = focusId;
  while (cur) {
    out.add(cur);
    cur = predBySucc.get(cur);
  }
  return out;
}

function ancestorChain(id: string, parentId: Map<string, string>): Set<string> {
  const out = new Set<string>();
  let cur: string | undefined = id;
  while (cur) {
    out.add(cur);
    cur = parentId.get(cur);
  }
  return out;
}

/** Default anchor: first critical activity, else first activity with a dependency, else first activity. */
export function defaultPathFocusActivityId(root: GuidanceTreeNode): string | null {
  const { activities } = indexActivityGraph(root);
  if (activities.length === 0) return null;
  return (
    activities.find((a) => a.critical)?.id ??
    activities.find((a) => a.dep)?.id ??
    activities[0]!.id
  );
}

/** Node ids to keep at full opacity; null when path filter is off. */
export function computePathHighlight(
  root: GuidanceTreeNode,
  modes: GuidancePathMode[],
  focusActivityId: string | null
): Set<string> | null {
  if (modes.length === 0) {
    return null;
  }

  const graph = indexActivityGraph(root);
  const needsChain = modes.includes('forward') || modes.includes('backward');
  const focus =
    focusActivityId && graph.activities.some((a) => a.id === focusActivityId)
      ? focusActivityId
      : needsChain
        ? defaultPathFocusActivityId(root)
        : null;

  let activityIds = new Set<string>();

  if (modes.includes('critical')) {
    const markCritical = (node: GuidanceTreeNode) => {
      if (node.critical) activityIds.add(node.id);
      (node.children ?? []).forEach(markCritical);
    };
    markCritical(root);
  }

  if (focus) {
    if (modes.includes('forward')) {
      Array.from(forwardClosure(focus, graph.succByPred)).forEach((id) => activityIds.add(id));
    }
    if (modes.includes('backward')) {
      Array.from(backwardClosure(focus, graph.predBySucc)).forEach((id) => activityIds.add(id));
    }
    if (modes.includes('forward') || modes.includes('backward')) {
      activityIds.add(focus);
    }
  }

  const highlighted = new Set<string>([root.id]);
  Array.from(activityIds).forEach((actId) => {
    ancestorChain(actId, graph.parentId).forEach((id) => highlighted.add(id));
  });

  return highlighted;
}

export function pathFilterOpacity(
  nodeId: string,
  highlight: Set<string> | null,
  displayMode: GuidancePathDisplayMode = 'dim'
): number {
  if (!highlight || displayMode === 'hide') return 1;
  return highlight.has(nodeId) ? 1 : GUIDANCE_PATH_DIM_OPACITY;
}

/** When display mode is hide, remove tree rows outside the path highlight set. */
export function filterGuidanceRowsByPathHighlight(
  rows: FlatGuidanceRow[],
  highlight: Set<string> | null,
  displayMode: GuidancePathDisplayMode
): FlatGuidanceRow[] {
  if (displayMode !== 'hide' || !highlight) return rows;
  return rows.filter((row) => highlight.has(row.node.id));
}

export function filterDependencyPaths(
  paths: SvgPathSpec[],
  rows: FlatGuidanceRow[],
  highlight: Set<string> | null
): SvgPathSpec[] {
  if (!highlight) return paths;
  const byId = Object.fromEntries(rows.map((r) => [r.node.id, r.node]));
  return paths.filter((p) => {
    const childId = p.id.startsWith('dep-') ? p.id.slice(4) : p.id;
    const child = byId[childId];
    if (!child?.dep) return false;
    return highlight.has(childId) && highlight.has(child.dep);
  });
}

export function togglePathModeList(
  modes: GuidancePathMode[],
  mode: GuidancePathMode
): GuidancePathMode[] {
  return modes.includes(mode) ? modes.filter((m) => m !== mode) : [...modes, mode];
}
